import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectorRef, Component, ComponentFactoryResolver, ComponentRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { pdfDefaultOptions } from 'ngx-extended-pdf-viewer';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription, interval } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Signatories, Signatures } from 'src/app/contract/shared/contract.model';
import { ContractService } from 'src/app/contract/shared/contract.service';
import { DocuSignMultiSignerPayload, DocuSignService, DocuSignSignerInfo, DocuSignWorkflowInfo } from 'src/app/contract/shared/docusign.service';
import { UserService } from 'src/app/shared/user.service';
import { ConfirmDialogComponent } from '../../../confirm-dialog/confirm-dialog.component';
import { RecallDialogComponent } from '../../../recall-dialog/recall-dialog.component';
import { AddSignatoryComponent } from './add-signatory/add-signatory.component';
import { CustomComponent } from './custom/custom.component';


@Component({
  selector: 'app-contract-signature',
  templateUrl: './contract-signature.component.html',
  styleUrls: ['./contract-signature.component.scss']
})
export class ContractSignatureComponent implements OnInit, OnDestroy {
  @Input() pdfURL: string | Observable<string>;
  @Input() contractId: number;
  @Output() refreshContract = new EventEmitter<void>();
  signatures: Signatures = new Signatures();
  p: any;
  isLoading = false;
  actualPdfUrl: string;
  dragging = false;
  isSidebarFixed = false;
  scrollThreshold = 200;

  readonly signatureGuideSteps = [
    {
      title: 'Add Signers',
      description: 'Select "Add New" to include every signer required on the document.'
    },
    {
      title: 'Arrange Signing Order',
      description: 'Drag and drop the signers in this list to arrange the exact signing order. The signatory at the top will be the first to sign.'
    },
    {
      title: 'Place Signature Fields',
      description: 'Drag each signer from this panel onto the document to place their signature fields.'
    },
    {
      title: 'Review and Adjust',
      description: 'Review the fields on the document and use the edit or delete icons to update a signer\'s details if needed.'
    },
    {
      title: 'Send for Signing',
      description: 'Press "Send" once all required signatories are tagged on the document and the sequence looks correct.'
    }
  ];

  private readonly STORAGE_KEY_PREFIX = 'contract_signatories_';

  @HostListener('window:scroll', [])
  onWindowScroll() {
    const scrollY = window.scrollY || document.documentElement.scrollTop;
    this.isSidebarFixed = scrollY > this.scrollThreshold && (this.isContractApproved || this.isContractSignature);
  }

  @ViewChild('container', { read: ViewContainerRef }) container: ViewContainerRef;

  selectedComponent: ComponentRef<CustomComponent> = null;
  customComponents: ComponentRef<CustomComponent>[] = [];
  totalPages: number = 0;
  isContractApproved: boolean = false;
  isContractSignature: boolean = false;
  isContractRejected: boolean = false;
  isContractTerminated: boolean = false; // Status 11 - Terminated
  isContractRecalled: boolean = false;   // Envelope voided/recalled
  isPendingSignature: boolean = false; // Status 5 - Pending Signature
  rejectionInfo: { name: string; reason: string }[] = [];
  terminationReason: string = ''; // Termination reason for terminated contracts
  recallReason: string = '';      // Recall (voided) reason from DocuSign
  signingDetails: any;
  useDocuSign: boolean = false; // Toggle between existing and DocuSign method

  // Polling properties for DocuSign workflow status
  private workflowPollingSubscription: Subscription | null = null;
  private readonly POLL_INTERVAL_SECONDS = 10; // Poll every 10 seconds
  private envelopeId: string | null = null;
  private readonly STORAGE_KEY_ENVELOPE_PREFIX = 'docusign_envelope_';

  constructor(
    private contractService: ContractService,
    private docuSignService: DocuSignService,
    private dialog: MatDialog,
    private resolver: ComponentFactoryResolver,
    private cdr: ChangeDetectorRef,
    private toast: ToastrService,
    private userService: UserService,
  ) {
    pdfDefaultOptions.assetsFolder = 'bleeding-edge';
    this.signatures.Signatories = [];
  }

  onDragStart() {
    this.dragging = true;
    this.cdr.detectChanges(); // Force UI update
  }

  onDragEnd() {
    this.dragging = false;
    this.cdr.detectChanges(); // Force UI update
  }

  async ngOnInit() {
    this.isLoading = true;
    if (this.pdfURL && typeof this.pdfURL === 'object' && 'subscribe' in this.pdfURL) {
      this.pdfURL.subscribe(async url => {
        try {
          this.actualPdfUrl = url;
        } catch (error) {
          this.toast.error('Error loading PDF');
          console.error(error);
        }
      });
    }

    // Get contract details to check status
    const contract = await this.contractService.getContract(this.contractId).catch(() => {
      this.toast.error('Error fetching contract');
      return null;
    });

    if (contract) {
      this.isContractApproved = [3].includes(contract['statusId']);
      this.isContractRejected = [4].includes(contract['statusId']);
      this.isContractTerminated = contract['statusId'] === 11; // Status 11 is terminated
      this.isContractSignature = [5, 6].includes(contract['statusId']); // Only status 5 (pending) and 6 (signed) show signature screen
      this.isPendingSignature = contract['statusId'] === 5; // Status 5 is pending signature

      // Signed through DocuSign, but the signed copy was never attached (the
      // Connect webhook did not reach us). Fetch it now so the preview and
      // download show the signed file instead of the unsigned one.
      if (contract['statusId'] === 6 && !contract['documentIdSigned'] && contract['envelopeId']) {
        this.storeSignedCopy(true).then((stored) => {
          if (stored) {
            this.refreshContract.emit();
            this.toast.success('Signed copy fetched from DocuSign. Please refresh this page to see it.');
          }
        });
      }
      
      // If contract is terminated, get termination reason and signing details
      if (this.isContractTerminated) {
        // Store termination reason from contract (check multiple possible fields)
        this.terminationReason = contract['terminationReason'] || 
                                 contract['reason'] || 
                                 contract['voidReason'] ||
                                 contract['voidedReason'] ||
                                 contract['actionName'] || // Sometimes stored in actionName
                                 '';
        
        // Fetch signing details to show signers list
        const tenantId = this.userService.loggedinUser?.tenantId;
        if (tenantId) {
          // Try DocuSign workflowInfo first
          this.docuSignService.getWorkflowInfo(tenantId, this.contractId).subscribe(
            (response) => {
              if (response && response.isSuccess && response.body) {
                this.useDocuSign = true;
                this.signingDetails = this.transformDocuSignWorkflowInfo(response.body);
                
                // Check if termination reason is in workflow info
                if (!this.terminationReason && response.body['voidReason']) {
                  this.terminationReason = response.body['voidReason'];
                }
                if (!this.terminationReason && response.body['voidedReason']) {
                  this.terminationReason = response.body['voidedReason'];
                }
                
                this.cdr.detectChanges();
              } else {
                // Not a DocuSign contract, use regular signing details
                this.getRegularSigningDetails();
              }
            },
            (error) => {
              // Not a DocuSign contract or API error, use regular signing details
              this.getRegularSigningDetails();
            }
          );
        } else {
          this.getRegularSigningDetails();
        }
      }

      // Extract rejection info from contract object if available (fallback)
      if (this.isContractRejected && contract['rejectedBy'] && contract['reason']) {
        this.rejectionInfo = [{
          name: contract['rejectedBy'] || 'Unknown',
          reason: contract['reason'] || 'No reason provided'
        }];
      }

      // Fetch signing details for rejected contracts to get rejection/recall information and load signatories
      if (this.isContractRejected) {
        const tenantId = this.userService.loggedinUser?.tenantId;
        if (tenantId) {
          // Try DocuSign workflowInfo first
          this.docuSignService.getWorkflowInfo(tenantId, this.contractId).subscribe(
            (response) => {
              if (response && response.isSuccess && response.body) {
                const workflowStatus = response.body.status?.toLowerCase();

                if (workflowStatus === 'voided') {
                  // This is a recalled contract
                  this.isContractRecalled = true;
                  this.recallReason = response.body.voidedReason || this.recallReason || 'Contract recalled via DocuSign';

                  // Update flags for recalled/rejected state
                  this.isContractRejected = true;
                  this.isContractSignature = false;
                  this.isContractApproved = false;
                  this.isPendingSignature = false;
                  this.isContractTerminated = false;
                } else {
                  // Extract rejection information from DocuSign
                  this.extractRejectionInfo(response.body);
                }

                // Transform and store signing details
                this.signingDetails = this.transformDocuSignWorkflowInfo(response.body);
                // Load signatories from signing details
                this.loadSignatoriesFromSigningDetails(response.body);
              } else {
                // Not a DocuSign contract or no data, use regular signing details
                this.getRegularSigningDetailsForRejected();
              }
            },
            (error) => {
              // Not a DocuSign contract or API error, use regular signing details
              this.getRegularSigningDetailsForRejected();
            }
          );
        } else {
          this.getRegularSigningDetailsForRejected();
        }
      }

      if (this.isContractSignature) {
        // Try to detect if this is a DocuSign contract by attempting to fetch workflowInfo
        // If successful, use DocuSign workflowInfo; otherwise fallback to regular signing details
        const tenantId = this.userService.loggedinUser?.tenantId;
        if (tenantId) {
          // Try DocuSign workflowInfo first
          this.docuSignService.getWorkflowInfo(tenantId, this.contractId).subscribe(
            (response) => {
              if (response && response.isSuccess && response.body) {
                // This is a DocuSign contract
                this.useDocuSign = true;
                this.signingDetails = this.transformDocuSignWorkflowInfo(response.body);
                
                // Extract rejection information if contract is rejected
                if (this.isContractRejected) {
                  this.extractRejectionInfo(response.body);
                } else {
                  // Also check if any signers have declined status
                  const signers = response.body.recipients?.signers || [];
                  const hasDeclinedSigners = signers.some(s => s.status?.toLowerCase() === 'declined');
                  if (hasDeclinedSigners) {
                    this.extractRejectionInfo(response.body);
                  }
                }

                // Check workflow status and handle accordingly
                const workflowStatus = response.body.status?.toLowerCase();

                // Start polling if contract is still pending (status 5) and workflow is not completed/declined
                if (contract['statusId'] === 5 &&
                  workflowStatus !== 'completed' &&
                  workflowStatus !== 'completed/signed' &&
                  workflowStatus !== 'signed' &&
                  workflowStatus !== 'declined' &&
                  workflowStatus !== 'voided') {
                  this.startWorkflowPolling();
                } else {
                  // Contract is completed, declined, or already updated - check and update if needed
                  this.checkAndUpdateSigningDetails(response.body);
                }
              } else {
                // Not a DocuSign contract or no data, use regular signing details
                this.getRegularSigningDetails();
              }
            },
            (error) => {
              // Not a DocuSign contract or API error, use regular signing details
              this.getRegularSigningDetails();
            }
          );
        } else {
          this.getRegularSigningDetails();
        }
      } else if (this.isContractApproved) {
        // Load signatories from local storage if contract is in approved state
        this.loadSignatoriesFromStorage();
      }
    }
    setTimeout(() => {
      this.isLoading = false;
    }, 2000);
    // Get total pages from PDF
    const pdfViewer = document.querySelector('ngx-extended-pdf-viewer');
    pdfViewer?.addEventListener('pagesloaded', (event: any) => {
      this.totalPages = event.detail;
    });
  }

  private getStorageKey(): string {
    if (!this.contractId) {
      console.warn('Contract ID is not set when trying to access storage');
      return '';
    }
    return `${this.STORAGE_KEY_PREFIX}${this.contractId}`;
  }

  private getEnvelopeStorageKey(): string {
    if (!this.contractId) {
      return '';
    }
    return `${this.STORAGE_KEY_ENVELOPE_PREFIX}${this.contractId}`;
  }

  private saveEnvelopeIdToStorage(envelopeId: string): void {
    const storageKey = this.getEnvelopeStorageKey();
    if (storageKey) {
      localStorage.setItem(storageKey, envelopeId);
    }
  }

  private loadEnvelopeIdFromStorage(): string | null {
    const storageKey = this.getEnvelopeStorageKey();
    if (storageKey) {
      return localStorage.getItem(storageKey);
    }
    return null;
  }

  private clearEnvelopeIdFromStorage(): void {
    const storageKey = this.getEnvelopeStorageKey();
    if (storageKey) {
      localStorage.removeItem(storageKey);
    }
  }

  private loadSignatoriesFromStorage(): void {
    const storageKey = this.getStorageKey();
    if (!storageKey) {
      this.signatures.Signatories = [];
      return;
    }

    const storedData = localStorage.getItem(storageKey);
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        // Ensure all signatories have Status property
        this.signatures.Signatories = parsedData.map((signatory: any) => ({
          ...signatory,
          Status: signatory.Status || 'Pending',
          RejectionReason: signatory.RejectionReason || '',
          Remarks: signatory.Remarks || ''
        }));
      } catch (error) {
        console.error('Error parsing stored signatories:', error);
        this.signatures.Signatories = [];
      }
    } else {
      this.signatures.Signatories = [];
    }
  }

  private saveSignatoriesToStorage(): void {
    const storageKey = this.getStorageKey();
    if (!storageKey) {
      return;
    }
    localStorage.setItem(storageKey, JSON.stringify(this.signatures.Signatories));
  }

  addNewSignatory(name: string, email: string, isAdditional: boolean = false) {
    // Check if signatory with this email already exists
    const existingSignatory = this.signatures.Signatories.find(s => s.EmailId.toLowerCase() === email.toLowerCase());

    if (existingSignatory) {
      this.toast.warning('A signatory with this email already exists.');
      return false;
    }

    const newSignatory = {
      Name: name,
      EmailId: email,
      SignerType: 1,
      Coordinates: [],
      isAdditional: isAdditional,
      Status: 'Pending',
      RejectionReason: '',
      Remarks: ''
    };
    this.signatures.Signatories.push(newSignatory);
    this.saveSignatoriesToStorage();
    this.cdr.detectChanges();

    return true;
  }


  openAddSignatory(): void {
    if (!this.isContractApproved && !this.isContractRejected) {
      this.toast.warning('Cannot add signatories. Contract must be approved first.');
      return;
    }

    const dialogRef = this.dialog.open(AddSignatoryComponent, {
      enterAnimationDuration: '400ms',
      exitAnimationDuration: '500ms',
      width: '30%',
      data: {
        addFunction: (name: string, email: string, isAdditional: boolean) => this.addNewSignatory(name, email, isAdditional),
        id: this.contractId,
        existingSignatories: this.signatures.Signatories
      }
    });
  }

  onDragEnded2(event) {
    const droppedIndex = event.item.data.index;

    // 1) Get the screen coordinates where the user dropped
    const dropPositionX = event.clientX || event.dropPoint.x;
    const dropPositionY = event.clientY || event.dropPoint.y;
    console.log("Drop Position:", dropPositionX, dropPositionY);

    // 2) Find which PDF page was under the drop
    const elementUnderCursor = document.elementFromPoint(dropPositionX, dropPositionY);
    const pdfPage = elementUnderCursor?.closest('.page') as HTMLElement;
    if (!pdfPage) {
      console.warn("Couldn't find a .page element under cursor. Defaulting to first page?");
      return;
    }

    // 3) Get the bounding rectangle of that page
    const pageRect = pdfPage.getBoundingClientRect();
    console.log("Clicked PDF Page Rectangle:", pageRect);

    // 4) Extract the 'data-page-number' attribute if needed
    const pageNumber = pdfPage.getAttribute('data-page-number') || '1';
    console.log("Page number:", pageNumber);

    // 5) Calculate position relative to the top-left of that page
    const adjustedX = dropPositionX - pageRect.x;
    const adjustedY = dropPositionY - pageRect.y;

    // 6) Convert to PDF coordinates
    const pdfWidth = 612;
    const pdfHeight = 792;

    // Compute scale factors from the rendered page size to PDF points
    const scaleX = pdfWidth / pageRect.width;
    const scaleY = pdfHeight / pageRect.height;

    // X in PDF space
    const pdfX = adjustedX * scaleX;
    // Y in PDF space (flip Y axis so PDF coordinate 0 starts at bottom)
    const pdfY = pdfHeight - (adjustedY * scaleY);

    console.log("PDF Coordinates:", pdfX, pdfY, "on page:", pageNumber);

    // 7) Store the PDF coordinates in your data model
    const userData: Signatories = event.item.dropContainer.data[droppedIndex];
    const index = this.signatures.Signatories.findIndex(x => x.EmailId === userData.EmailId);

    if (index >= 0) {
      // If the signatory exists, set the coordinates instead of pushing
      this.signatures.Signatories[index].Coordinates = [{
        Left: pdfX - 50,
        Height: pdfY,
        // Width: 100,
        Page: 'CURRENT',
        PageNumber: pageNumber
      }];
    } else {
      // If the signatory doesn't exist (shouldn't happen), create new entry
      userData.Coordinates = [{
        Left: pdfX - 50,
        Height: pdfY,
        // Width: 100,
        Page: 'CURRENT',
        PageNumber: pageNumber
      }];
      this.signatures.Signatories.push(userData);
    }

    // 8) Create or position the overlay component for the dropped signature
    this.createOverlayComponent(pdfPage, adjustedX, adjustedY, userData);
  }


  private createOverlayComponent(pdfPage: HTMLElement, adjustedX: number, adjustedY: number, userData: Signatories) {
    const factory = this.resolver.resolveComponentFactory(CustomComponent);
    const componentRef: ComponentRef<CustomComponent> = this.container.createComponent(factory);

    // Set absolute position using the calculated adjusted coordinates.
    componentRef.instance.dropPositionX = adjustedX - 50 + 'px';
    componentRef.instance.dropPositionY = adjustedY - 50 + 'px';

    componentRef.instance.data = userData;
    componentRef.instance.signatories = this.signatures.Signatories;

    // Add to tracking array
    this.customComponents.push(componentRef);

    // Instead of appending to the generic container, append to the specific pdfPage element.
    pdfPage.appendChild(componentRef.location.nativeElement);
    this.cdr.detectChanges();

    componentRef.instance.onSelect.subscribe(() => {
      this.selectComponent(componentRef);
    });

    componentRef.instance.onPropertiesChange.subscribe((e) => {
      const index = this.signatures.Signatories.findIndex(x => x.EmailId === e.signatory);
      if (index >= 0) {
        this.signatures.Signatories[index].Coordinates[0].Page = e.pageOption;
        this.signatures.Signatories[index].Coordinates[0].PageNumber = e.pageNumbers;
      }
      console.log(this.signatures);
    });


    componentRef.instance.onRemove.subscribe(() => {
      const index = this.customComponents.indexOf(componentRef);
      if (index > -1) {
        componentRef.destroy();
        this.customComponents.splice(index, 1);
        if (this.selectedComponent === componentRef) {
          this.selectedComponent = null;
        }
        this.cdr.detectChanges();
      }
    });
  }

  selectComponent(componentRef: ComponentRef<CustomComponent>) {
    if (this.selectedComponent) {
      this.selectedComponent.instance.isSelected = false;
    }

    this.selectedComponent = componentRef;
    componentRef.instance.isSelected = true;
    this.cdr.detectChanges();
  }

  onPdfDrop(event) {
    if (!this.isContractApproved && !this.isContractRejected) {
      this.toast.warning('Cannot add signatures. Contract must be approved first.');
      return;
    }
    this.onDragEnded2(event);
  }

  onSignerDrop(event) {
    console.log('Dropped on signer area');
  }

  get hasDroppedSignatures(): boolean {
    return this.signatures.Signatories.some(signatory =>
      signatory.Coordinates && signatory.Coordinates.length > 0
    );
  }

  async sendForSign() {
    if (this.useDocuSign) {
      await this.sendForSignDocuSign();
    } else {
      await this.sendForSignDefault();
    }
  }

  async sendForSignDefault() {
    this.isLoading = true;
    try {
      if (!this.isContractApproved && !this.isContractRejected) {
        this.toast.warning('Cannot send for signature. Contract must be approved first.');
        return;
      }

      // For rejected contracts, coordinates are optional since PDF viewer is not shown
      // For approved contracts, require at least one signature placed
      if (!this.isContractRejected && !this.hasDroppedSignatures) {
        this.toast.warning('Please place at least one signature on the document.');
        return;
      }

      // Create a new signatures object with only the signatories that have coordinates
      const filteredSignatures = {
        ...this.signatures,
        Signatories: this.signatures.Signatories.filter(signatory =>
          signatory.Coordinates && signatory.Coordinates.length > 0
        )
      };

      const response = await this.contractService.sendForSignature(this.contractId, filteredSignatures);

      if (response) {
        // Check if the response contains the specific error message
        if (response === 'Contract sent for signatures...\nWorkFlowId : 0') {
          this.toast.error('Failed to initialize signature workflow. Please try again later.');
        } else {
          try {
            await this.contractService.updateContractStatus(this.contractId, 5, this.userService.loggedinUser.id, 'Send for Signature');
            this.toast.success('Document successfully sent for signatures.');
            this.isContractApproved = false;
            this.isContractSignature = true;
            // Clear local storage for this contract after successful sending
            localStorage.removeItem(this.getStorageKey());
            this.getSigningDetails();
            this.refreshContract.emit();
          } catch (statusError) {
            console.error('Error updating contract status:', statusError);
            // Even if status update fails, document was sent for signature
            this.toast.warning('Document sent for signature but status update failed. Please refresh the page.');
          }
        }
      } else {
        this.toast.error('Failed to send document for signature. Please try again.');
      }
    } catch (error: any) {
      console.error('Error sending document for signature:', error);

      // Handle different types of errors
      if (error.status === 0) {
        this.toast.error('Network error. Please check your internet connection.');
      } else if (error.status === 400) {
        this.toast.error('Invalid request. Please check the signature positions and try again.');
      } else if (error.status === 401) {
        this.toast.error('Session expired. Please login again.');
      } else if (error.status === 403) {
        this.toast.error('You do not have permission to send this document for signature.');
      } else if (error.status === 404) {
        this.toast.error('Contract not found. Please refresh the page.');
      } else if (error.status === 500) {
        this.toast.error('Server error occurred. Please try again later.');
      } else {
        // If error message exists in response, show it, otherwise show generic message
        const errorMessage = error.error?.message || error.message || 'An unexpected error occurred. Please try again.';
        this.toast.error(errorMessage);
      }
    } finally {
      // Ensure loading is always set to false when operation completes
      this.isLoading = false;
    }
  }

  async sendForSignDocuSign() {
    this.isLoading = true;
    try {
      if (!this.isContractApproved && !this.isContractRejected) {
        this.toast.warning('Cannot send for signature. Contract must be approved first.');
        this.isLoading = false;
        return;
      }

      // For rejected contracts, coordinates are optional since PDF viewer is not shown
      // For approved contracts, require at least one signature placed
      if (!this.isContractRejected && !this.hasDroppedSignatures) {
        this.toast.warning('Please place at least one signature on the document.');
        this.isLoading = false;
        return;
      }

      // Get signatories with coordinates (or all signatories for rejected contracts)
      const signatoriesWithCoordinates = this.isContractRejected
        ? this.signatures.Signatories
        : this.signatures.Signatories.filter(signatory =>
            signatory.Coordinates && signatory.Coordinates.length > 0
          );

      if (signatoriesWithCoordinates.length === 0) {
        this.toast.warning('Please add at least one signatory.');
        this.isLoading = false;
        return;
      }

      // Get tenantId
      const tenantId = this.userService.loggedinUser?.tenantId;
      if (!tenantId) {
        this.toast.error('Tenant ID is required. Please login again.');
        this.isLoading = false;
        return;
      }

      // Validate and convert coordinates to DocuSign format for all signatories
      const signers: DocuSignSignerInfo[] = [];
      const validationErrors: string[] = [];

      signatoriesWithCoordinates.forEach((signatory, index) => {
        // Validate email
        const email = (signatory.EmailId || '').trim();
        if (!email) {
          validationErrors.push(`Signatory ${index + 1} (${signatory.Name || 'Unknown'}) has no email address`);
          return;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          validationErrors.push(`Signatory ${index + 1} (${signatory.Name || 'Unknown'}) has invalid email format: ${email}`);
          return;
        }

        // Validate name
        const name = (signatory.Name || '').trim();
        if (!name) {
          validationErrors.push(`Signatory ${index + 1} has no name`);
          return;
        }

        const coord = signatory.Coordinates[0]; // Use first coordinate

        // Extract page number - ensure it's a valid number string
        let pageNumber = coord.PageNumber || coord.Page || '1';
        // If PageNumber is not a valid number, default to 1
        if (pageNumber === 'CURRENT' || pageNumber === 'ALL' || !/^\d+$/.test(pageNumber)) {
          pageNumber = '1';
        }

        // Convert PDF coordinates to DocuSign format
        // PDF coordinates: Left (X from left), Height (Y from bottom)
        // DocuSign expects: xCordinate (X from left), yCordinate (Y from top)
        const xCordinate = Math.round(coord.Left || 0).toString();

        // Convert Y coordinate from bottom-origin to top-origin
        // Height in PDF is from bottom, DocuSign expects from top
        const pdfHeight = 792; // Standard PDF page height in points (Letter size)
        const yCordinate = Math.round(pdfHeight - (coord.Height || 0)).toString();

        signers.push({
          signerName: name,
          signerEmail: email,
          pageNumber: pageNumber,
          xCordinate: xCordinate,
          yCordinate: yCordinate,
          routingOrder: signers.length + 1 // Set routing order based on valid signers
        });
      });

      // Check for validation errors
      if (validationErrors.length > 0) {
        this.toast.error(`Validation errors: ${validationErrors.join('; ')}`);
        this.isLoading = false;
        return;
      }

      if (signers.length === 0) {
        this.toast.error('No valid signers found. Please check signatory details.');
        this.isLoading = false;
        return;
      }

      // Prepare payload for multi-signer request
      const payload: DocuSignMultiSignerPayload = {
        Signatories: signers.map((signer, index) => ({
          SignerName: signer.signerName,
          SignerEmail: signer.signerEmail,
          PageNumber: signer.pageNumber,
          XCoordinate: signer.xCordinate,
          YCoordinate: signer.yCordinate,
          RoutingOrder: signer.routingOrder || (index + 1)
        }))
      };

      try {
        // Send single request with all signers
        const response = await this.docuSignService.sendDocumentForSignatureMultiple(
          tenantId,
          this.contractId,
          payload
        ).toPromise();

        if (response && response.isSuccess) {
          // Extract envelope ID from response
          let envelopeId: string | null = null;
          if (response.message) {
            const match = response.message.match(/Envelope ID[:\s]+([^\s,]+)/i);
            if (match) envelopeId = match[1];
          }
          if (!envelopeId && response.body) {
            if (typeof response.body === 'string') {
              const match = response.body.match(/Envelope ID[:\s]+([^\s,]+)/i);
              if (match) envelopeId = match[1];
              else envelopeId = response.body.trim();
            } else if (typeof response.body === 'object' && response.body !== null) {
              envelopeId = (response.body as any).envelopeId || (response.body as any).envelopeID || null;
            }
          }

          try {
            await this.contractService.updateContractStatus(
              this.contractId,
              5,
              this.userService.loggedinUser.id,
              'Send for Signature (DocuSign)'
            );

            let successMessage = `Document successfully sent to ${signers.length} signer(s) via DocuSign.`;
            if (envelopeId) {
              successMessage += ` Envelope ID: ${envelopeId}`;
              // Store envelope ID for polling
              this.envelopeId = envelopeId;
              this.saveEnvelopeIdToStorage(envelopeId);
            }

            this.toast.success(successMessage);
            this.isContractApproved = false;
            this.isContractSignature = true;
            // Set useDocuSign flag so getSigningDetails uses workflowInfo API
            this.useDocuSign = true;
            // Clear local storage for this contract after successful sending
            localStorage.removeItem(this.getStorageKey());
            // Fetch signing details using DocuSign workflowInfo API
            this.getSigningDetails();
            // Start polling to check for completion
            if (envelopeId) {
              this.startWorkflowPolling();
            }
            this.refreshContract.emit();
          } catch (statusError) {
            console.error('Error updating contract status:', statusError);
            this.toast.warning('Document sent via DocuSign but status update failed. Please refresh the page.');
          }
        } else {
          // Handle API error response
          const errorMsg = response?.message || 'Unknown error';
          this.toast.error(`Failed to send document via DocuSign: ${errorMsg}`);
        }
      } catch (apiError: any) {
        // Handle API errors
        console.error('Error sending document via DocuSign:', apiError);

        if (apiError?.error?.message) {
          this.toast.error(apiError.error.message);
        } else if (apiError?.message) {
          this.toast.error(apiError.message);
        } else {
          this.toast.error('Failed to send document via DocuSign. Please try again.');
        }
      }
    } catch (error: any) {
      console.error('Error sending document via DocuSign:', error);

      // Handle API response errors
      if (error?.error?.message) {
        this.toast.error(error.error.message);
      } else if (error?.message) {
        this.toast.error(error.message);
      } else {
        this.toast.error('Failed to send document via DocuSign. Please try again.');
      }
    } finally {
      this.isLoading = false;
    }
  }


  getSigningDetails() {
    if (this.isContractSignature) {
      // Check if this is a DocuSign contract by trying to get workflow info
      const tenantId = this.userService.loggedinUser?.tenantId;
      if (tenantId && this.useDocuSign) {
        // Use DocuSign workflowInfo API
        this.docuSignService.getWorkflowInfo(tenantId, this.contractId).subscribe(
          (response) => {
            if (response && response.isSuccess && response.body) {
              this.signingDetails = this.transformDocuSignWorkflowInfo(response.body);
              
              // Extract rejection information if contract is rejected
              if (this.isContractRejected) {
                this.extractRejectionInfo(response.body);
              } else {
                // Also check if any signers have declined status
                const signers = response.body.recipients?.signers || [];
                const hasDeclinedSigners = signers.some(s => s.status?.toLowerCase() === 'declined');
                if (hasDeclinedSigners) {
                  this.extractRejectionInfo(response.body);
                }
              }
            } else {
              // Fallback to regular signing details
              this.getRegularSigningDetails();
            }
          },
          (error) => {
            console.error('Error fetching DocuSign workflow info:', error);
            // Fallback to regular signing details
            this.getRegularSigningDetails();
          }
        );
      } else {
        // Use regular signing details for non-DocuSign contracts
        this.getRegularSigningDetails();
      }
    }
  }

  private getRegularSigningDetails() {
    this.contractService.getSigningDetails(this.contractId).then((res: any) => {
      if (res.IsSuccess && res.Response && res.Response.length > 0) {
        this.signingDetails = res.Response[0];
        
        // Check if contract status has changed to rejected
        this.contractService.getContract(this.contractId).then((contract: any) => {
          if (contract) {
            const isNowRejected = [4].includes(contract['statusId']);
            const wasRejected = this.isContractRejected;
            
            // If status changed to rejected, update flags
            if (!wasRejected && isNowRejected) {
              this.isContractRejected = true;
              this.isContractSignature = false;
              this.isContractApproved = false;
              
              // Extract rejection information and load signatories
              if (this.signingDetails?.Signatories) {
                this.extractRejectionInfoFromSigningDetails(this.signingDetails);
                this.loadSignatoriesFromSigningDetails(this.signingDetails);
              }
              
              this.cdr.detectChanges();
            } else if (isNowRejected && this.signingDetails?.Signatories) {
              // Contract is already rejected, just extract info
              this.extractRejectionInfoFromSigningDetails(this.signingDetails);
            }
          }
        }).catch((error) => {
          console.error('Error checking contract status:', error);
        });
      }
    }).catch((error) => {
      console.error('Error fetching signing details:', error);
    });
  }

  private getRegularSigningDetailsForRejected() {
    this.contractService.getSigningDetails(this.contractId).then((res: any) => {
      if (res.IsSuccess && res.Response && res.Response.length > 0) {
        this.signingDetails = res.Response[0];
        
        // Extract rejection information and load signatories
        if (this.isContractRejected && this.signingDetails?.Signatories) {
          this.extractRejectionInfoFromSigningDetails(this.signingDetails);
          this.loadSignatoriesFromSigningDetails(this.signingDetails);
        }
      }
    }).catch((error) => {
      console.error('Error fetching signing details:', error);
    });
  }

  /**
   * Extract rejection information from DocuSign workflow info
   */
  private extractRejectionInfo(workflowInfo: DocuSignWorkflowInfo): void {
    this.rejectionInfo = [];
    const signers = workflowInfo.recipients?.signers || [];
    
    signers.forEach((signer) => {
      if (signer.status?.toLowerCase() === 'declined' && signer.declinedReason) {
        this.rejectionInfo.push({
          name: signer.name || signer.email || 'Unknown',
          reason: signer.declinedReason
        });
      }
    });
  }

  /**
   * Extract rejection information from regular signing details
   */
  private extractRejectionInfoFromSigningDetails(signingDetails: any): void {
    this.rejectionInfo = [];
    
    if (signingDetails.Signatories && Array.isArray(signingDetails.Signatories)) {
      signingDetails.Signatories.forEach((signatory: any) => {
        if (signatory.Status === 'Rejected' && signatory.Remarks) {
          // Parse remarks to extract name and reason
          // Format might be "Declined: {reason}" or just the reason
          let reason = signatory.Remarks;
          if (reason.startsWith('Declined: ')) {
            reason = reason.substring('Declined: '.length);
          }
          
          this.rejectionInfo.push({
            name: signatory.Name || signatory.EmailID || 'Unknown',
            reason: reason
          });
        }
      });
    }
  }

  /**
   * Load signatories from signing details for rejected contracts
   */
  private loadSignatoriesFromSigningDetails(signingDetails: any): void {
    let signatoriesList: any[] = [];

    // Handle DocuSign workflow info format
    if (signingDetails.recipients && signingDetails.recipients.signers) {
      const transformed = this.transformDocuSignWorkflowInfo(signingDetails);
      signatoriesList = transformed.Signatories || [];
    }
    // Handle regular signing details format
    else if (signingDetails.Signatories) {
      signatoriesList = signingDetails.Signatories;
    }

    if (!signatoriesList || signatoriesList.length === 0) {
      return;
    }

    this.signatures.Signatories = signatoriesList.map((signatory: any) => {
      // Find rejection reason for this signatory
      let rejectionReason = '';
      if (signatory.Status === 'Rejected' && signatory.Remarks) {
        rejectionReason = signatory.Remarks;
        if (rejectionReason.startsWith('Declined: ')) {
          rejectionReason = rejectionReason.substring('Declined: '.length);
        }
      }

      return {
        Name: signatory.Name || signatory.EmailID || '',
        EmailId: signatory.EmailID || signatory.EmailId || '',
        SignerType: 1,
        Coordinates: signatory.Coordinates || [],
        isAdditional: false,
        Status: signatory.Status || 'Pending',
        RejectionReason: rejectionReason,
        Remarks: signatory.Remarks || ''
      };
    });

    // Save to storage so they persist
    this.saveSignatoriesToStorage();
    this.cdr.detectChanges();
  }


  /**
   * Transform DocuSign workflow info response to match the UI format
   */
  private transformDocuSignWorkflowInfo(workflowInfo: DocuSignWorkflowInfo): any {
    const signers = workflowInfo.recipients?.signers || [];

    return {
      Signatories: signers.map((signer) => {
        // Determine the date to show (signed, declined, or delivered)
        let dateToShow = null;
        if (signer.signedDateTime) {
          dateToShow = signer.signedDateTime;
        } else if (signer.declinedDateTime) {
          dateToShow = signer.declinedDateTime;
        } else if (signer.deliveredDateTime) {
          dateToShow = signer.deliveredDateTime;
        }

        // Build remarks from declined reason or note
        let remarks = '';
        if (signer.declinedReason) {
          remarks = `Declined: ${signer.declinedReason}`;
        } else if (signer.note) {
          remarks = signer.note;
        }

        return {
          Name: signer.name || '',
          EmailID: signer.email || '',
          Status: this.mapDocuSignStatus(signer.status || 'pending'),
          SignedDate: dateToShow,
          Remarks: remarks
        };
      })
    };
  }

  private transformDocuSignDetails(docusignData: any): any {
    if (docusignData.Signatories) {
      return docusignData;
    }
    const signatories = Array.isArray(docusignData.signers) ? docusignData.signers : [];

    return {
      Signatories: signatories.map((signer: any) => ({
        Name: signer.name || signer.signerName || '',
        EmailID: signer.email || signer.signerEmail || '',
        Status: this.mapDocuSignStatus(signer.status || signer.envelopeStatus || 'Pending'),
        SignedDate: signer.signedDateTime || signer.signedDate || null,
        Remarks: signer.remarks || signer.note || ''
      }))
    };
  }

  /**
   * Asks the backend to download the completed envelope's signed PDF from
   * DocuSign and attach it to the contract. Returns true when stored.
   * Never throws: a failure here must not block the status update.
   */
  private async storeSignedCopy(quiet: boolean = false): Promise<boolean> {
    const tenantId = this.userService.loggedinUser?.tenantId;
    if (!tenantId) {
      return false;
    }
    try {
      const response = await this.docuSignService.storeSignedDocument(tenantId, this.contractId).toPromise();
      return !!(response && response.isSuccess);
    } catch (error) {
      console.error('Failed to store signed document from DocuSign:', error);
      if (!quiet) {
        this.toast.warning('Signing completed, but the signed copy could not be fetched from DocuSign yet. Please refresh the page in a moment.');
      }
      return false;
    }
  }

  private mapDocuSignStatus(docusignStatus: string): string {
    const statusMap: { [key: string]: string } = {
      'completed': 'Signed',
      'signed': 'Signed',
      'delivered': 'Pending',
      'sent': 'Pending',
      'declined': 'Rejected',
      'voided': 'Rejected',
      'pending': 'Pending',
      'created': 'Pending',
      'autosigned': 'Signed'
    };

    const normalizedStatus = docusignStatus?.toLowerCase() || 'pending';
    return statusMap[normalizedStatus] || 'Pending';
  }


  getStatusClass(status: string): string {
    switch (status) {
      case 'Signed':
        return 'status-signed';
      case 'Pending':
        return 'status-pending';
      case 'Rejected':
        return 'status-rejected';
      default:
        return 'status-default';
    }
  }

  /**
   * Start polling workflow info to check for completion
   */
  private startWorkflowPolling(): void {
    // Stop any existing polling
    this.stopWorkflowPolling();

    const tenantId = this.userService.loggedinUser?.tenantId;
    if (!tenantId || !this.contractId) {
      console.warn('Cannot start workflow polling: missing tenantId or contractId');
      return;
    }

    // Poll every N seconds
    this.workflowPollingSubscription = interval(this.POLL_INTERVAL_SECONDS * 1000)
      .pipe(
        switchMap(() => this.docuSignService.getWorkflowInfo(tenantId, this.contractId))
      )
      .subscribe(
        (response) => {
          if (response && response.isSuccess && response.body) {
            this.checkAndUpdateSigningDetails(response.body);
            
            // Also check current contract status to catch any status changes
            this.contractService.getContract(this.contractId).then((contract: any) => {
              if (contract) {
                const contractStatusId = contract['statusId'];
                
                // Stop polling if contract is terminated (status 11)
                if (contractStatusId === 11) {
                  this.stopWorkflowPolling();
                  this.isContractTerminated = true;
                  this.isContractSignature = false;
                  this.isContractApproved = false;
                  this.isContractRejected = false;
                  this.isPendingSignature = false;
                  
                  // Store termination reason
                  this.terminationReason = contract['terminationReason'] || contract['reason'] || '';
                  this.cdr.detectChanges();
                  return;
                }
                
                const wasRejected = this.isContractRejected;
                const isNowRejected = [4].includes(contractStatusId);
                
                // If status changed to rejected, update flags and reload signatories
                if (!wasRejected && isNowRejected) {
                  this.isContractRejected = true;
                  this.isContractSignature = false;
                  this.isContractApproved = false;
                  
                  // Extract rejection information
                  this.extractRejectionInfo(response.body);
                  
                  // Load signatories from signing details
                  this.loadSignatoriesFromSigningDetails(response.body);
                  
                  this.cdr.detectChanges();
                }
              }
            }).catch((error) => {
              console.error('Error checking contract status:', error);
            });
          }
        },
        (error) => {
          console.error('Error in workflow polling:', error);
          // Don't stop polling on single errors, just log them
        }
      );
  }

  /**
   * Stop workflow polling
   */
  private stopWorkflowPolling(): void {
    if (this.workflowPollingSubscription) {
      this.workflowPollingSubscription.unsubscribe();
      this.workflowPollingSubscription = null;
    }
  }

  /**
   * Check workflow status and update contract if completed or declined
   */
  private async checkAndUpdateSigningDetails(workflowInfo: DocuSignWorkflowInfo): Promise<void> {
    // Update signing details
    this.signingDetails = this.transformDocuSignWorkflowInfo(workflowInfo);
    this.cdr.detectChanges();

    const status = workflowInfo.status?.toLowerCase();

    // Check if envelope status is voided (recalled)
    if (status === 'voided') {
      // Stop polling
      this.stopWorkflowPolling();

      // Mark contract as recalled in the UI
      this.isContractRecalled = true;
      this.isContractTerminated = false;

      // Capture recall reason from workflow info (voidedReason)
      this.recallReason = workflowInfo.voidedReason || this.recallReason || 'Contract recalled via DocuSign';

      // Treat the contract as Rejected (status 4), but only update if backend hasn't already done it
      try {
        const currentContract = await this.contractService.getContract(this.contractId);
        if (currentContract && currentContract['statusId'] !== 4) {
          await this.contractService.updateContractStatus(
            this.contractId,
            4,
            this.userService.loggedinUser?.id,
            this.recallReason
          );
        }

        // Update local flags to reflect rejected/recalled status
        this.isContractRejected = true;
        this.isContractSignature = false;
        this.isContractApproved = false;
        this.isPendingSignature = false;

        // Refresh contract data
        this.refreshContract.emit();
        this.cdr.detectChanges();
      } catch (error) {
        console.error('Error updating contract status after recall (void):', error);
      }
    }
    // Check if envelope status is declined (rejected by signer)
    else if (status === 'declined') {
      // Stop polling
      this.stopWorkflowPolling();

      // Update contract status to 4 (Rejected) if not already updated
      try {
        const currentContract = await this.contractService.getContract(this.contractId);
        if (currentContract && currentContract['statusId'] !== 4) {
          // Get declined reason from signers
          const declinedSigners = workflowInfo.recipients?.signers?.filter(s => s.status?.toLowerCase() === 'declined') || [];
          const declinedReasons = declinedSigners
            .map(s => s.declinedReason ? `${s.name}: ${s.declinedReason}` : s.name)
            .join('; ');

          await this.contractService.updateContractStatus(
            this.contractId,
            4,
            this.userService.loggedinUser?.id,
            declinedReasons ? `Contract declined via DocuSign - ${declinedReasons}` : 'Contract declined via DocuSign'
          );

          // Update local flags to reflect rejected status
          this.isContractRejected = true;
          this.isContractSignature = false;
          this.isContractApproved = false;

          // Extract rejection information
          this.extractRejectionInfo(workflowInfo);
          
          // Load signatories from signing details
          this.loadSignatoriesFromSigningDetails(workflowInfo);

          // Refresh contract data
          this.refreshContract.emit();
          this.toast.warning('Contract has been declined by one or more signers.');
          this.cdr.detectChanges();
        } else if (currentContract && currentContract['statusId'] === 4) {
          // Contract is already rejected, update flags and load signatories
          this.isContractRejected = true;
          this.isContractSignature = false;
          this.isContractApproved = false;
          
          // Extract rejection information
          this.extractRejectionInfo(workflowInfo);
          
          // Load signatories from signing details
          this.loadSignatoriesFromSigningDetails(workflowInfo);
          
          this.cdr.detectChanges();
        }
      } catch (error) {
        console.error('Error updating contract status:', error);
        this.toast.warning('Contract was declined, but failed to update contract status. Please refresh the page.');
      }
    }
    // Check if envelope status is completed
    else if (status === 'completed' || status === 'completed/signed' || status === 'signed') {
      // Stop polling
      this.stopWorkflowPolling();

      // Pull the signed copy from DocuSign ourselves. The Connect webhook does
      // the same, but only when DocuSign is configured to call this server.
      const stored = await this.storeSignedCopy();

      // Storing the copy already marks the contract Signed on the server. Only
      // fall back to a plain status update when that did not happen.
      try {
        const currentContract = await this.contractService.getContract(this.contractId);
        if (currentContract && currentContract['statusId'] !== 6) {
          await this.contractService.updateContractStatus(
            this.contractId,
            6,
            this.userService.loggedinUser?.id,
            'Contract signed via DocuSign'
          );
        }

        // Update local flags
        this.isContractSignature = true;
        this.isContractApproved = false;

        // Refresh contract data
        this.refreshContract.emit();
        this.toast.success(stored
          ? 'Contract signing completed! The signed document is now available, Please refresh this page.'
          : 'Contract signing completed! Please refresh this page.');
      } catch (error) {
        console.error('Error updating contract status:', error);
        this.toast.warning('Signing completed, but failed to update contract status. Please refresh the page.');
      }
    }
  }

  ngOnDestroy() {
    // Stop workflow polling
    this.stopWorkflowPolling();

    // Clean up the object URL when component is destroyed
    if (this.actualPdfUrl) {
      URL.revokeObjectURL(this.actualPdfUrl);
    }
  }

  editSignatory(index: number) {
    const dialogRef = this.dialog.open(AddSignatoryComponent, {
      enterAnimationDuration: '400ms',
      exitAnimationDuration: '500ms',
      width: '30%',
      data: {
        isEdit: true,
        id: this.contractId,
        currentSignatory: this.signatures.Signatories[index],
        addFunction: (name, email) => {
          // Check if another signatory (not the one being edited) already has this email
          const duplicateSignatory = this.signatures.Signatories.find((s, i) =>
            i !== index && s.EmailId.toLowerCase() === email.toLowerCase()
          );

          if (duplicateSignatory) {
            this.toast.warning('A signatory with this email already exists.');
            return false;
          }

          const oldEmail = this.signatures.Signatories[index].EmailId;
          const existingCoordinates = this.signatures.Signatories[index].Coordinates;

          // Update the signatory details while preserving coordinates
          this.signatures.Signatories[index] = {
            Name: name,
            EmailId: email,
            SignerType: 1,
            Coordinates: existingCoordinates
          };

          // Update the component reference with new data
          this.customComponents.forEach(componentRef => {
            if (componentRef.instance.data.EmailId === oldEmail) {
              componentRef.instance.data = this.signatures.Signatories[index];
              this.cdr.detectChanges();
            }
          });

          // Save updated signatories to local storage
          this.saveSignatoriesToStorage();

          return true;
        }
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.cdr.detectChanges();
      }
    });
  }

  deleteSignatory(index: number) {
    // Validate index
    if (index < 0 || index >= this.signatures.Signatories.length) {
      this.toast.error('Invalid signatory index');
      return;
    }

    // Get the signatory being deleted before opening dialog
    const signatoryToDelete = this.signatures.Signatories[index];
    if (!signatoryToDelete) {
      this.toast.error('Signatory not found');
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Delete Signatory',
        message: 'Are you sure you want to delete this signatory?',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        confirmButtonColor: 'warn',
        icon: 'heroExclamationTriangle',
        confirmFunction: async () => {
          // This function is called before the dialog closes
          // The actual deletion logic is in afterClosed subscription
        }
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true || result === 'true') {
        try {
          // Get the signatory email for matching
          const deletedEmailId = signatoryToDelete.EmailId;

          // Remove the signatory from the array
          this.signatures.Signatories.splice(index, 1);

          // Save to storage
          this.saveSignatoriesToStorage();

          // Find and remove all corresponding CustomComponents
          const componentsToRemove: ComponentRef<CustomComponent>[] = [];
          for (let i = this.customComponents.length - 1; i >= 0; i--) {
            const componentRef = this.customComponents[i];
            if (componentRef && componentRef.instance && componentRef.instance.data) {
              const componentEmailId = componentRef.instance.data.EmailId;

              if (componentEmailId === deletedEmailId) {
                componentsToRemove.push(componentRef);
                this.customComponents.splice(i, 1);
              }
            }
          }

          // Destroy all matching components
          componentsToRemove.forEach((componentRef) => {
            try {
              // Remove from DOM if it's still attached
              if (componentRef.location && componentRef.location.nativeElement) {
                const nativeElement = componentRef.location.nativeElement;
                if (nativeElement.parentNode) {
                  nativeElement.parentNode.removeChild(nativeElement);
                }
              }

              // Destroy the component
              componentRef.destroy();
            } catch (error) {
              console.error('Error destroying component:', error);
            }
          });

          // If the deleted component was selected, clear the selection
          if (this.selectedComponent && this.selectedComponent.instance &&
            this.selectedComponent.instance.data &&
            this.selectedComponent.instance.data.EmailId === deletedEmailId) {
            this.selectedComponent = null;
          }

          this.cdr.detectChanges();
          this.toast.success('Signatory deleted successfully');
        } catch (error) {
          console.error('Error in deleteSignatory:', error);
          this.toast.error('Error deleting signatory: ' + (error.message || 'Unknown error'));
        }
      }
    });
  }

  onDrop(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.signatures.Signatories, event.previousIndex, event.currentIndex);
    this.saveSignatoriesToStorage();
  }

  /**
   * Void the DocuSign envelope (Recall)
   * This method calls the voidEnvelope API to recall a contract that is pending signature
   */
  async voidEnvelope() {
    const tenantId = this.userService.loggedinUser?.tenantId;
    if (!tenantId) {
      this.toast.error('Tenant ID is required. Please login again.');
      return;
    }

    if (!this.contractId) {
      this.toast.error('Contract ID is missing.');
      return;
    }

    // Show recall dialog with void reason textarea
    const dialogRef = this.dialog.open(RecallDialogComponent, {
      width: '500px',
      data: {
        contractId: this.contractId
      }
    });

    dialogRef.afterClosed().subscribe(async (voidReason) => {
      // If voidReason is null, user cancelled the dialog
      if (voidReason) {
        this.isLoading = true;
        try {
          // Stop workflow polling before voiding to prevent status conflicts
          this.stopWorkflowPolling();
          
          const response = await this.docuSignService.voidEnvelope(tenantId, this.contractId, voidReason).toPromise();

          if (response && response.isSuccess) {
            this.toast.success('Contract recalled successfully.');
            
            // Backend updates status to 11 (terminated) when voided
            // Refresh contract to get updated status from backend
            const contract = await this.contractService.getContract(this.contractId).catch(() => null);
            if (contract) {
              const contractStatusId = contract['statusId'];
              
              // Update status flags based on actual contract status from backend
              this.isContractApproved = [3].includes(contractStatusId);
              this.isContractRejected = [4].includes(contractStatusId);
              this.isContractSignature = [5, 6].includes(contractStatusId);
              this.isPendingSignature = contractStatusId === 5;
              
              // If status is 11 (terminated), update flags accordingly
              if (contractStatusId === 11) {
                this.isContractTerminated = true;
                this.isContractSignature = false;
                this.isContractApproved = false;
                this.isContractRejected = false;
                this.isPendingSignature = false;
                
                // Store termination reason - use the void reason from dialog if contract doesn't have it
                this.terminationReason = contract['terminationReason'] || 
                                       contract['reason'] || 
                                       contract['voidReason'] ||
                                       contract['voidedReason'] ||
                                       voidReason || // Use the void reason from the recall dialog
                                       '';
                
                // Fetch signing details to show signers list
                const tenantId = this.userService.loggedinUser?.tenantId;
                if (tenantId) {
                  this.docuSignService.getWorkflowInfo(tenantId, this.contractId).subscribe(
                    (response) => {
                      if (response && response.isSuccess && response.body) {
                        this.useDocuSign = true;
                        this.signingDetails = this.transformDocuSignWorkflowInfo(response.body);
                        
                        // Check if termination reason is in workflow info
                        if (!this.terminationReason && response.body['voidReason']) {
                          this.terminationReason = response.body['voidReason'];
                        }
                        if (!this.terminationReason && response.body['voidedReason']) {
                          this.terminationReason = response.body['voidedReason'];
                        }
                        
                        this.cdr.detectChanges();
                      } else {
                        this.getRegularSigningDetails();
                      }
                    },
                    (error) => {
                      this.getRegularSigningDetails();
                    }
                  );
                } else {
                  this.getRegularSigningDetails();
                }
              }
            }
            
            // Refresh contract data
            this.refreshContract.emit();
            this.cdr.detectChanges();
          } else {
            const errorMsg = response?.message || 'Failed to recall contract';
            this.toast.error(errorMsg);
          }
        } catch (error: any) {
          console.error('Error voiding envelope:', error);
          const errorMsg = error?.error?.message || error?.message || 'Failed to recall contract. Please try again.';
          this.toast.error(errorMsg);
        } finally {
          this.isLoading = false;
        }
      }
    });
  }
}
